import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LifeBuoy, Heart, Activity, Briefcase, Users, Star, BookOpen, Shield, Compass, Gift,
  ExternalLink, Phone, Pencil, Trash2, Plus, Settings2,
} from "lucide-react";
import { api, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const ICONS = {
  "life-buoy": LifeBuoy, heart: Heart, activity: Activity, briefcase: Briefcase,
  users: Users, star: Star, "book-open": BookOpen, shield: Shield, compass: Compass, gift: Gift,
};
const ICON_KEYS = Object.keys(ICONS);

export default function Resources() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manage, setManage] = useState(false);
  const [dialog, setDialog] = useState(null); // {kind:'item'|'group', mode, form, id}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/resources");
      setGroups(data.groups || []);
    } catch (err) {
      console.error("Failed to load resources:", err);
      toast.error("Couldn't load resources. Please try again.");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { track("resources_viewed"); load(); }, [load]);

  const openAddItem = (groupId) => setDialog({ kind: "item", mode: "add", id: null, form: { group_id: groupId, name: "", description: "", url: "https://", phone: "" } });
  const openEditItem = (groupId, it) => setDialog({ kind: "item", mode: "edit", id: it.id, form: { group_id: groupId, name: it.name, description: it.description || "", url: it.url, phone: it.phone || "" } });
  const openAddGroup = () => setDialog({ kind: "group", mode: "add", id: null, form: { title: "", blurb: "", icon: "life-buoy" } });
  const openEditGroup = (g) => setDialog({ kind: "group", mode: "edit", id: g.id, form: { title: g.title, blurb: g.blurb || "", icon: g.icon || "life-buoy" } });

  const setField = (k, v) => setDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  const save = async () => {
    const { kind, mode, id, form } = dialog;
    try {
      if (kind === "item") {
        if (!form.name.trim()) return toast.error("Resource name is required.");
        if (mode === "add") await api.post("/resources/items", form);
        else await api.put(`/resources/items/${id}`, form);
      } else {
        if (!form.title.trim()) return toast.error("Group title is required.");
        if (mode === "add") await api.post("/resources/groups", form);
        else await api.put(`/resources/groups/${id}`, form);
      }
      toast.success(mode === "add" ? "Added" : "Saved");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    }
  };

  const delItem = async (it) => {
    if (!window.confirm(`Remove "${it.name}"?`)) return;
    await api.delete(`/resources/items/${it.id}`);
    toast.success("Removed");
    load();
  };
  const delGroup = async (g) => {
    if (!window.confirm(`Delete the "${g.title}" group and all its resources?`)) return;
    await api.delete(`/resources/groups/${g.id}`);
    toast.success("Group deleted");
    load();
  };

  return (
    <div className="space-y-10" data-testid="resources-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">For you & your sisters</p>
          <h1 className="font-heading text-3xl lg:text-5xl mt-1">Resources</h1>
          <p className="text-[#291B2D]/60 mt-2 max-w-2xl">
            A hand-picked directory of trusted organizations for women — support, health, money,
            and community. Every link opens a live, external website.
          </p>
        </div>
        {isOwner && (
          <button
            onClick={() => setManage((m) => !m)}
            data-testid="resources-manage-toggle"
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-smooth ${manage ? "bg-[#291B2D] text-white" : "bg-[#F7F2EA] text-[#291B2D] hover:bg-[#efe7d9]"}`}
          >
            <Settings2 size={15} /> {manage ? "Done" : "Manage"}
          </button>
        )}
      </div>

      <div data-testid="resources-disclaimer" className="rounded-2xl bg-[#F7F2EA] border border-[#E5DED4] p-5 lg:p-6">
        <p className="text-sm text-[#59535B] leading-relaxed max-w-3xl">
          These organizations are independent of Sisterhood+ and are shared for your convenience.
          If you are in immediate danger, call <a href="tel:911" className="underline font-semibold">911</a>.
          In emotional crisis, call or text <a href="tel:988" className="underline font-semibold">988</a>.
        </p>
      </div>

      <Link
        to="/therapists"
        data-testid="resources-therapists-cta"
        className="block rounded-3xl bg-[#291B2D] text-white p-6 lg:p-8 card-soft hover:brightness-110 transition-smooth"
      >
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <Users className="text-[#C89B55]" size={22} strokeWidth={1.75} />
          </span>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">New</p>
            <h2 className="font-heading text-2xl leading-tight mt-0.5">Find a Therapist</h2>
            <p className="text-sm text-white/70 mt-1">Culturally responsive, licensed professionals — welcoming all women, with deep experience supporting women of color.</p>
          </div>
          <ExternalLink size={18} className="text-white/40 shrink-0" />
        </div>
      </Link>

      {manage && (
        <button onClick={openAddGroup} data-testid="resources-add-group"
          className="inline-flex items-center gap-2 rounded-full bg-[#291B2D] text-white px-5 py-2.5 text-sm font-medium hover:brightness-110 transition-smooth">
          <Plus size={16} /> Add category
        </button>
      )}

      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => <div key={i} className="h-32 bg-[#F1EADE] rounded-3xl animate-pulse" />)}
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 card-soft text-center">
          <p className="font-heading text-xl">No resources yet</p>
          {isOwner && <p className="text-sm text-[#291B2D]/60 mt-2">Turn on Manage to add your first category.</p>}
        </div>
      ) : (
        groups.map((group, gi) => {
          const Icon = ICONS[group.icon] || LifeBuoy;
          return (
            <section key={group.id} data-testid={`resource-group-${group.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-11 h-11 rounded-2xl bg-[#291B2D] flex items-center justify-center shrink-0">
                  <Icon className="text-[#C89B55]" size={20} strokeWidth={1.75} />
                </span>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl leading-tight">{group.title}</h2>
                  <p className="text-sm text-[#291B2D]/55">{group.blurb}</p>
                </div>
                {manage && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => openAddItem(group.id)} data-testid={`add-item-${group.id}`} title="Add resource"
                      className="w-9 h-9 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center"><Plus size={16} /></button>
                    <button onClick={() => openEditGroup(group)} data-testid={`edit-group-${group.id}`} title="Edit category"
                      className="w-9 h-9 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center"><Pencil size={15} /></button>
                    <button onClick={() => delGroup(group)} data-testid={`delete-group-${group.id}`} title="Delete category"
                      className="w-9 h-9 rounded-full bg-[#FBEAEA] hover:bg-[#f6dede] text-[#9B2C2C] flex items-center justify-center"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {group.items.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: (gi * 0.02) + i * 0.03 }}
                    className="relative group bg-white rounded-3xl card-soft p-6 hover:-translate-y-0.5 transition-smooth flex flex-col h-full"
                  >
                    <a href={item.url} target="_blank" rel="noopener noreferrer"
                      onClick={() => track("resource_opened", { group: group.id, name: item.name })}
                      data-testid={`resource-link-${group.id}-${i}`} className="flex flex-col h-full">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-heading text-lg leading-snug">{item.name}</p>
                        <ExternalLink size={16} className="text-[#291B2D]/30 group-hover:text-[#C89B55] transition-smooth shrink-0 mt-1" />
                      </div>
                      <p className="text-sm text-[#291B2D]/60 leading-relaxed mt-2 flex-1">{item.description}</p>
                    </a>
                    {item.phone && (
                      <a href={`tel:${item.phone.replace(/[^0-9]/g, "")}`} data-testid={`resource-phone-${group.id}-${i}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3D6B52] hover:underline w-fit">
                        <Phone size={14} /> {item.phone}
                      </a>
                    )}
                    {manage && (
                      <div className="absolute top-4 right-4 flex items-center gap-1.5">
                        <button onClick={() => openEditItem(group.id, item)} data-testid={`edit-item-${item.id}`} title="Edit"
                          className="w-8 h-8 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center"><Pencil size={14} /></button>
                        <button onClick={() => delItem(item)} data-testid={`delete-item-${item.id}`} title="Remove"
                          className="w-8 h-8 rounded-full bg-[#FBEAEA] hover:bg-[#f6dede] text-[#9B2C2C] flex items-center justify-center"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </section>
          );
        })
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="resource-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {dialog?.mode === "add" ? "Add" : "Edit"} {dialog?.kind === "group" ? "category" : "resource"}
            </DialogTitle>
          </DialogHeader>
          {dialog?.kind === "item" ? (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="field-name" value={dialog.form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Organization name" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="field-description" value={dialog.form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Short, warm description" rows={2} />
              </div>
              <div>
                <Label>Website URL</Label>
                <Input data-testid="field-url" value={dialog.form.url} onChange={(e) => setField("url", e.target.value)} placeholder="https://example.org" />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input data-testid="field-phone" value={dialog.form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="1-800-000-0000" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={dialog.form.group_id} onValueChange={(v) => setField("group_id", v)}>
                  <SelectTrigger data-testid="field-group"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : dialog?.kind === "group" ? (
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input data-testid="field-title" value={dialog.form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Category title" />
              </div>
              <div>
                <Label>Blurb</Label>
                <Textarea data-testid="field-blurb" value={dialog.form.blurb} onChange={(e) => setField("blurb", e.target.value)} placeholder="One-line description" rows={2} />
              </div>
              <div>
                <Label>Icon</Label>
                <Select value={dialog.form.icon} onValueChange={(v) => setField("icon", v)}>
                  <SelectTrigger data-testid="field-icon"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ICON_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} data-testid="resource-dialog-save" className="bg-[#291B2D] text-white hover:bg-[#3a2740]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
